// (portfolio-20261027) Pure math for the Portfolio panel — no React, no fetch,
// so it can be tested with plain node. Longs only.

export const PERIODS = [['today', 'Today'], ['7d', '7D'], ['30d', '30D'], ['all', 'All']]

// Start of a period in ms. 'today' = local midnight.
export function periodStart(p, now = Date.now()) {
  if (p === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.getTime() }
  if (p === '7d') return now - 7 * 86400e3
  if (p === '30d') return now - 30 * 86400e3
  return 0
}

// Spot ledger from swap history ([{timestamp(ms), side, mint, tokenAmount, solAmount}]).
// Average cost: a sell realizes proceeds − (avg cost × tokens sold). A sell with
// no recorded buy (bought before the history window) books no realized PnL.
export function spotLedger(history) {
  const byMint = {}
  const events = []
  const list = (Array.isArray(history) ? history : [])
    .filter(t => t && t.mint && (t.side === 'buy' || t.side === 'sell'))
    .slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
  for (const t of list) {
    const m = byMint[t.mint] || (byMint[t.mint] = { boughtSol: 0, soldSol: 0, boughtTok: 0, soldTok: 0, realizedSol: 0, cost: 0, tok: 0, lastTs: 0 })
    const sol = +t.solAmount || 0, amt = +t.tokenAmount || 0
    m.lastTs = Math.max(m.lastTs, t.timestamp || 0)
    if (t.side === 'buy') {
      m.boughtSol += sol; m.boughtTok += amt; m.cost += sol; m.tok += amt
    } else {
      m.soldSol += sol; m.soldTok += amt
      if (m.tok > 0 && amt > 0) {
        const f = Math.min(1, amt / m.tok)
        const costOut = m.cost * f
        const pnl = sol - costOut
        m.cost -= costOut; m.tok = Math.max(0, m.tok - amt)
        m.realizedSol += pnl
        events.push({ t: t.timestamp || 0, mint: t.mint, pnlSol: pnl })
      }
    }
  }
  return { byMint, events }
}

// Sum a field over events at/after `since`.
export function sumSince(events, since, field) {
  let s = 0
  for (const e of events || []) if ((e.t || 0) >= since && Number.isFinite(e[field])) s += e[field]
  return s
}

// Paper sells → realized events (SOL).
export function paperEvents(hist) {
  return (hist || []).filter(h => h && h.side === 'sell' && Number.isFinite(h.pnl)).map(h => ({ t: h.t, mint: h.token, pnlSol: h.pnl }))
}

// HL fills → realized events (USD, net of fee). Fills that close nothing are skipped.
export function hlEvents(fills) {
  return (fills || []).filter(f => f && !f._provisional && (+f.closedPnl || 0) !== 0)
    .map(f => ({ t: +f.time || 0, sym: f.symbol, pnlUsd: (+f.closedPnl || 0) - (+f.fee || 0) }))
}

// Open paper position at a mark (SOL mcap units). No fees.
export function paperOpen(pos, mark) {
  if (!pos || !(pos.units > 0)) return null
  const costSol = pos.costSol || 0
  if (!(mark > 0)) return { costSol, valueSol: null, pnlSol: null, pct: null }
  const valueSol = pos.units * mark
  const pnlSol = valueSol - costSol
  return { costSol, valueSol, pnlSol, pct: costSol > 0 ? (pnlSol / costSol) * 100 : null }
}

// Open bot position from one bot_status slot. Null when flat or not long.
export function botOpen(slot) {
  const s = slot?.status || {}
  const basis = s.load?.blendedBasis || s.pilot?.mcap || 0
  if (!(basis > 0)) return null
  const dir = s.load?.direction || s.pilot?.direction || s.tradeDirection || 'LONG'
  if (dir === 'SHORT') return null
  const last = s.lastMcap > 0 ? s.lastMcap : 0
  const size = s.position?.sizeSol || s.load?.totalSizeSol || s.pilot?.sizeSol || 0
  const pct = last > 0 ? ((last - basis) / basis) * 100 : null
  return { token: slot.token || s.currentToken || null, basis, last, sizeSol: size, pct,
    pnlSol: pct != null && size > 0 ? size * pct / 100 : null, state: slot.state || s.state || '' }
}

// Hide rows worth less than minUsd (rows with unknown value stay).
export function hideDust(rows, minUsd = 1) {
  return (rows || []).filter(r => !(Number.isFinite(r.valueUsd)) || r.valueUsd >= minUsd)
}

// Sort by 'value' or 'pnl' (desc); unknowns last.
export function sortRows(rows, key = 'value') {
  const f = key === 'pnl' ? (r => r.pnlUsd) : (r => r.valueUsd)
  return (rows || []).slice().sort((a, b) => {
    const x = f(a), y = f(b)
    const xa = Number.isFinite(x), ya = Number.isFinite(y)
    if (xa !== ya) return xa ? -1 : 1
    return xa ? y - x : 0
  })
}

// Newest-first merge of activity lists ([{t,...}]), capped.
export function mergeActivity(lists, limit = 50) {
  return [].concat(...lists.map(l => l || [])).filter(a => a && a.t > 0).sort((a, b) => b.t - a.t).slice(0, limit)
}
